import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  DataObject as DataObjectIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const ProjectExecutionSteps = () => {
  const { companyId, projectId, executionId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStep, setSelectedStep] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchExecutionDetails = async () => {
    setLoading(!execution); // Only show main loading on first load
    setError('');
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/executions/${executionId}`);
      setExecution(response.data.execution);
      
      // Auto-refresh for in-progress executions
      if (response.data.execution.status === 'in_progress' || response.data.execution.status === 'waiting_input') {
        setAutoRefresh(true);
      } else {
        setAutoRefresh(false);
      }
    } catch (err) {
      setError('Failed to fetch execution details');
      console.error('Execution details fetch error:', err);
      setAutoRefresh(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyId && projectId && executionId) {
      fetchExecutionDetails();
    }
  }, [companyId, projectId, executionId]);

  // Auto-refresh for in-progress executions
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchExecutionDetails();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const getStepIcon = (stepType, status) => {
    const iconProps = { fontSize: 'medium' };
    
    if (status === 'failed') return <ErrorIcon color="error" {...iconProps} />;
    if (status === 'completed') return <CheckCircleIcon color="success" {...iconProps} />;
    if (status === 'in_progress') return <ScheduleIcon color="warning" {...iconProps} />;
    if (status === 'waiting_input') return <InfoIcon color="info" {...iconProps} />;
    if (status === 'cancelled') return <CancelIcon color="disabled" {...iconProps} />;
    
    switch (stepType) {
      case 'file_upload':
      case 'file_input':
        return <AssignmentIcon color="action" {...iconProps} />;
      case 'ai_extraction':
      case 'data_extraction':
        return <DataObjectIcon color="action" {...iconProps} />;
      case 'api_call':
        return <CodeIcon color="action" {...iconProps} />;
      case 'data_transformation':
        return <AssessmentIcon color="action" {...iconProps} />;
      default:
        return <PlayArrowIcon color="action" {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'warning';
      case 'waiting_input': return 'info';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStepProgress = () => {
    if (!execution?.stepHistory) return 0;
    const completedSteps = execution.stepHistory.filter(step => step.status === 'completed').length;
    return Math.round((completedSteps / execution.stepHistory.length) * 100);
  };

  const getCurrentStepIndex = () => {
    if (!execution?.stepHistory) return -1;
    return execution.stepHistory.findIndex(step => 
      step.status === 'in_progress' || step.status === 'waiting_input' || step.status === 'failed'
    );
  };

  const handleViewStepDetails = (step) => {
    setSelectedStep(step);
    setDialogOpen(true);
  };

  const handleResumeExecution = () => {
    navigate(`/company/${companyId}/project/${projectId}/flow?resumeExecution=${executionId}`);
  };

  const handleCancelExecution = async () => {
    try {
      await api.post(`/companies/${companyId}/projects/${projectId}/executions/${executionId}/cancel`);
      fetchExecutionDetails(); // Refresh to show updated status
    } catch (error) {
      console.error('Failed to cancel execution:', error);
      alert('Failed to cancel execution');
    }
  };

  const renderStepsList = () => {
    if (!execution?.stepHistory) return null;

    return (
      <List>
        {execution.stepHistory.map((step, index) => {
          // Check for performance issues
          const hasPerformanceWarnings = step.performanceMetrics?.warnings && step.performanceMetrics.warnings.length > 0;
          const isSlowStep = step.duration && step.duration > 5000; // More than 5 seconds
          
          return (
            <ListItem
              key={index}
              sx={{
                mb: 1,
                border: step.status === 'in_progress' ? '2px solid' : '1px solid',
                borderColor: step.status === 'in_progress' ? 'warning.main' : 'divider',
                borderRadius: 2,
                bgcolor: step.status === 'in_progress' ? 'warning.50' : 'background.paper'
              }}
            >
              <ListItemIcon>
                {getStepIcon(step.stepType, step.status)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {step.stepName || `Step ${index + 1}`}
                    </Typography>
                    <Chip
                      label={step.status}
                      color={getStatusColor(step.status)}
                      size="small"
                      variant="outlined"
                    />
                    {step.status === 'in_progress' && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="warning.main">
                          Running...
                        </Typography>
                      </Box>
                    )}
                    {isSlowStep && step.status === 'completed' && (
                      <Chip
                        label="Slow"
                        size="small"
                        color="warning"
                        icon={<WarningIcon />}
                      />
                    )}
                    {hasPerformanceWarnings && (
                      <Chip
                        label={`${step.performanceMetrics.warnings.length} Warning${step.performanceMetrics.warnings.length > 1 ? 's' : ''}`}
                        size="small"
                        color="error"
                        icon={<WarningIcon />}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Type: {step.stepType} • Duration: {formatDuration(step.duration)}
                      {step.startedAt && (
                        <> • Started: {new Date(step.startedAt).toLocaleTimeString()}</>
                      )}
                    </Typography>
                    {step.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="body2">{step.error}</Typography>
                      </Alert>
                    )}
                    {hasPerformanceWarnings && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Performance Issues Detected:
                        </Typography>
                        {step.performanceMetrics.warnings.slice(0, 2).map((warning, idx) => (
                          <Typography key={idx} variant="caption" display="block">
                            • {warning.message}
                          </Typography>
                        ))}
                        {step.performanceMetrics.warnings.length > 2 && (
                          <Typography variant="caption" display="block" fontStyle="italic">
                            +{step.performanceMetrics.warnings.length - 2} more...
                          </Typography>
                        )}
                      </Alert>
                    )}
                    {step.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                {step.output && (
                  <IconButton
                    size="small"
                    onClick={() => handleViewStepDetails(step)}
                    title="View step details"
                  >
                    <VisibilityIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderTimelineView = () => {
    if (!execution?.stepHistory) return null;

    const currentStepIndex = getCurrentStepIndex();

    return (
      <Stepper activeStep={currentStepIndex} orientation="vertical">
        {execution.stepHistory.map((step, index) => {
          // Check for performance issues
          const hasPerformanceWarnings = step.performanceMetrics?.warnings && step.performanceMetrics.warnings.length > 0;
          const isSlowStep = step.duration && step.duration > 5000; // More than 5 seconds
          
          return (
            <Step key={index} completed={step.status === 'completed'}>
              <StepLabel
                error={step.status === 'failed'}
                icon={getStepIcon(step.stepType, step.status)}
              >
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight="medium">
                    {step.stepName || `Step ${index + 1}`}
                  </Typography>
                  <Chip
                    label={step.status}
                    color={getStatusColor(step.status)}
                    size="small"
                    variant="outlined"
                  />
                  {isSlowStep && step.status === 'completed' && (
                    <Chip
                      label="Slow"
                      size="small"
                      color="warning"
                      icon={<WarningIcon />}
                    />
                  )}
                  {hasPerformanceWarnings && (
                    <Chip
                      label={`${step.performanceMetrics.warnings.length} Warning${step.performanceMetrics.warnings.length > 1 ? 's' : ''}`}
                      size="small"
                      color="error"
                      icon={<WarningIcon />}
                    />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Box p={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Type:</strong> {step.stepType}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Duration:</strong> {formatDuration(step.duration)}
                  </Typography>
                  {step.startedAt && (
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Started:</strong> {new Date(step.startedAt).toLocaleString()}
                    </Typography>
                  )}
                  {step.description && (
                    <Typography variant="body2" gutterBottom>
                      {step.description}
                    </Typography>
                  )}
                  {step.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">{step.error}</Typography>
                    </Alert>
                  )}
                  {hasPerformanceWarnings && (
                    <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Performance Issues:
                      </Typography>
                      {step.performanceMetrics.warnings.slice(0, 2).map((warning, idx) => (
                        <Typography key={idx} variant="caption" display="block">
                          • {warning.message}
                        </Typography>
                      ))}
                      {step.performanceMetrics.warnings.length > 2 && (
                        <Typography variant="caption" display="block" fontStyle="italic">
                          +{step.performanceMetrics.warnings.length - 2} more...
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {step.output && (
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewStepDetails(step)}
                      sx={{ mt: 1 }}
                    >
                      View Details & Performance
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    );
  };

  if (loading) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (error || !execution) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Execution not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/company/${companyId}/project/${projectId}`)}
        >
          Back to Project
        </Button>
      </Box>
    );
  }

  return (
    <Box maxWidth={1200} mx="auto" mt={4} px={2}>
      <BreadcrumbsNav />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Execution Monitoring
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1" color="textSecondary">
              Execution #{executionId.slice(-8)} • {execution.projectName || 'Project'}
            </Typography>
            {autoRefresh && (
              <Chip 
                label="Auto-refreshing" 
                color="info" 
                size="small" 
                icon={<RefreshIcon />}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchExecutionDetails}
            disabled={loading}
          >
            Refresh
          </Button>
          {(execution.status === 'in_progress' || execution.status === 'waiting_input') && (
            <>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleResumeExecution}
                color="primary"
              >
                Resume
              </Button>
              <Button
                variant="outlined"
                startIcon={<StopIcon />}
                onClick={handleCancelExecution}
                color="error"
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Status Overview */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: getStatusColor(execution.status) + '.main', mx: 'auto', mb: 1 }}>
                {execution.status === 'completed' ? <CheckCircleIcon /> : 
                 execution.status === 'failed' ? <ErrorIcon /> :
                 execution.status === 'in_progress' ? <ScheduleIcon /> : <InfoIcon />}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {execution.status.replace('_', ' ').toUpperCase()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="primary">
                {getStepProgress()}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Progress
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={getStepProgress()} 
                sx={{ mt: 1, width: '80%', mx: 'auto' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {execution.stepHistory?.filter(step => step.status === 'completed').length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Completed Steps
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {formatDuration(
                  // For in-progress executions, prefer actualProcessingTime (completed steps only)
                  // For completed/failed executions, use actualProcessingTime if available, otherwise fall back to duration
                  (execution.status === 'in_progress' || execution.status === 'waiting_input') 
                    ? (execution.actualProcessingTime || 0)
                    : (execution.actualProcessingTime || execution.duration)
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Duration
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Started At:</Typography>
            <Typography variant="body1">
              {new Date(execution.startedAt).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Current Step:</Typography>
            <Typography variant="body1">
              {getCurrentStepIndex() >= 0 
                ? execution.stepHistory[getCurrentStepIndex()]?.stepName || `Step ${getCurrentStepIndex() + 1}`
                : 'All steps completed'
              }
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Step Details */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight="bold" mb={3}>
          Step-by-Step Progress
        </Typography>
        
        {/* Toggle between list and timeline view */}
        <Box mb={3}>
          <Button
            variant="outlined"
            startIcon={<TimelineIcon />}
            onClick={() => {}} // Could add view toggle here
            size="small"
          >
            Timeline View
          </Button>
        </Box>

        {renderTimelineView()}
      </Paper>

      {/* Step Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Step Details: {selectedStep?.stepName || selectedStep?.name}
        </DialogTitle>
        <DialogContent>
          {selectedStep && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Type:</Typography>
                  <Typography variant="body1">{selectedStep.stepType}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status:</Typography>
                  <Chip label={selectedStep.status} color={getStatusColor(selectedStep.status)} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Duration:</Typography>
                  <Typography variant="body1" fontWeight="bold">{formatDuration(selectedStep.duration)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Started:</Typography>
                  <Typography variant="body1">
                    {selectedStep.startedAt ? new Date(selectedStep.startedAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              {selectedStep.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">Error:</Typography>
                  <Typography variant="body2">{selectedStep.error}</Typography>
                </Alert>
              )}

              {/* Performance Metrics Section */}
              {selectedStep.performanceMetrics && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <AssessmentIcon color="primary" />
                    Performance Breakdown
                  </Typography>
                  
                  {/* Performance Warnings */}
                  {selectedStep.performanceMetrics.warnings && selectedStep.performanceMetrics.warnings.length > 0 && (
                    <Box mb={2}>
                      {selectedStep.performanceMetrics.warnings.map((warning, idx) => (
                        <Alert severity="warning" key={idx} sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">{warning.message}</Typography>
                          {warning.threshold && warning.actual && (
                            <Typography variant="caption" display="block" mt={0.5}>
                              Threshold: {warning.threshold}ms • Actual: {warning.actual}ms
                            </Typography>
                          )}
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {/* Time Breakdown */}
                  {selectedStep.performanceMetrics.breakdown && selectedStep.performanceMetrics.breakdown.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Time Breakdown:
                      </Typography>
                      <List dense>
                        {selectedStep.performanceMetrics.breakdown.map((item, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2">{item.operation}</Typography>
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Chip 
                                      label={`${item.percentage}%`} 
                                      size="small" 
                                      color={item.percentage > 50 ? 'error' : item.percentage > 25 ? 'warning' : 'default'}
                                      variant="outlined"
                                    />
                                    <Typography variant="body2" fontWeight="bold">
                                      {formatDuration(item.duration)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              secondary={item.details}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  {/* Detailed Metrics Grid */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Detailed Metrics:
                    </Typography>
                    <Grid container spacing={2}>
                      {/* File Operations */}
                      {(selectedStep.performanceMetrics.fileUploadTime || selectedStep.performanceMetrics.fileProcessingTime) && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="File Operations" size="small" />
                            </Divider>
                          </Grid>
                          {selectedStep.performanceMetrics.fileUploadTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Upload Time:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.fileUploadTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.fileProcessingTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Processing Time:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.fileProcessingTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.fileCount && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Files Processed:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedStep.performanceMetrics.fileCount}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.totalFileSize && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Total Size:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {(selectedStep.performanceMetrics.totalFileSize / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Network Operations */}
                      {(selectedStep.performanceMetrics.networkLatency || selectedStep.performanceMetrics.apiCallTime) && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="Network Operations" size="small" />
                            </Divider>
                          </Grid>
                          {selectedStep.performanceMetrics.networkLatency && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Network Latency:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.networkLatency)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.apiCallTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">API Call Time:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.apiCallTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.apiCallCount && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">API Calls:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedStep.performanceMetrics.apiCallCount}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* AI Operations */}
                      {selectedStep.performanceMetrics.aiProcessingTime && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="AI Operations" size="small" />
                            </Divider>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="textSecondary">AI Processing:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatDuration(selectedStep.performanceMetrics.aiProcessingTime)}
                            </Typography>
                          </Grid>
                          {selectedStep.performanceMetrics.aiModelUsed && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Model:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedStep.performanceMetrics.aiModelUsed}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.aiTokens && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Tokens Used:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedStep.performanceMetrics.aiTokens.toLocaleString()}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Pre-execution Operations */}
                      {(selectedStep.performanceMetrics.cacheCleanupTime || selectedStep.performanceMetrics.authenticationCheckTime || selectedStep.performanceMetrics.validationTime) && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="Pre-execution Operations" size="small" />
                            </Divider>
                          </Grid>
                          {selectedStep.performanceMetrics.cacheCleanupTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Cache Cleanup:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.cacheCleanupTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.authenticationCheckTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Auth Check:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.authenticationCheckTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.validationTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Validation:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.validationTime)}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Data Operations */}
                      {(selectedStep.performanceMetrics.dataTransformationTime || selectedStep.performanceMetrics.learningCorrectionsTime) && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="Data Operations" size="small" />
                            </Divider>
                          </Grid>
                          {selectedStep.performanceMetrics.dataTransformationTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Transformation:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.dataTransformationTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.learningCorrectionsTime && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Learning Corrections:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(selectedStep.performanceMetrics.learningCorrectionsTime)}
                              </Typography>
                            </Grid>
                          )}
                          {selectedStep.performanceMetrics.learningCorrectionsCount && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary">Corrections Sent:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedStep.performanceMetrics.learningCorrectionsCount}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* User Wait Time */}
                      {selectedStep.performanceMetrics.userWaitTime && (
                        <>
                          <Grid item xs={12}>
                            <Divider textAlign="left">
                              <Chip label="User Interaction" size="small" />
                            </Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">User Wait Time:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatDuration(selectedStep.performanceMetrics.userWaitTime)}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Paper>
                </Box>
              )}

              {selectedStep.output && (
                <Box>
                  <Typography variant="h6" gutterBottom>Output:</Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedStep.output, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectExecutionSteps; 